// atlas/apps/web/src/pages/KPIAnalyticsPage.tsx
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { pbAuthedFetch } from '../authHeaders';

interface KPI {
  key: string; label: string; category: string; unit: string;
  target: number; warn_threshold: number; direction: 'above' | 'below';
  current: number | null; status: 'green' | 'amber' | 'red';
  series: { period_start: string; value: number }[];
}
interface KPIResponse {
  ok: boolean;
  kpis: KPI[];
  health: { green: number; amber: number; red: number };
  signals: { kpi_key: string; label: string; message: string; severity: string }[];
  correlation: { raw_count: number; correlated_count: number; groups: { category: string; bucket: string; count: number }[] };
  benchmarks: { kpi_key: string; label: string; peer_low: number; peer_high: number }[];
}

function formatValue(kpi: KPI) {
  if (kpi.current == null) return '—';
  if (kpi.unit === 'pct') return `${kpi.current}%`;
  if (kpi.unit === 'minutes') return `${kpi.current}m`;
  if (kpi.unit === 'days') return `${kpi.current}d`;
  return `${kpi.current}`;
}

export function KPIAnalyticsPage() {
  const [selectedKpi, setSelectedKpi] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<KPIResponse>({
    queryKey: ['kpi-analytics'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/kpi');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  if (isLoading) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Analysis</p>
              <h1 className="pagehead__title">KPI Analytics</h1>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box"><div className="box__body">
            <div className="page-loading" role="status" aria-live="polite">Loading KPI scorecard…</div>
          </div></div>
        </div>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Analysis</p>
              <h1 className="pagehead__title">KPI Analytics</h1>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="state state--risk">
            <h3 className="state__title">Failed to load KPI data</h3>
            <p className="state__body">Could not connect to the KPI analytics API. Try refreshing the page.</p>
          </div>
        </div>
      </>
    );
  }

  const { kpis, health, signals, correlation, benchmarks } = data;
  const detail = kpis.find((k) => k.key === selectedKpi) || null;

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">Analysis</p>
            <h1 className="pagehead__title">KPI Analytics</h1>
            <div className="pagehead__facts">
              <span className="fact">Indicator scorecard with targets, thresholds, signals and benchmarks</span>
            </div>
          </div>
          <div className="box__head-actions">
            <button className="btn btn--secondary btn--sm" onClick={() => window.askStella?.('What KPIs are off target and why?')}>Ask Stella about this</button>
          </div>
        </div>
      </div>

      <div className="pagebody">
        <div className="cmd-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--sp-3)' }}>
          <div className="ind">
            <div className="ind__head"><p className="ind__abbr">GREEN</p><p className="ind__full">On target</p></div>
            <div className="ind__value"><p className="ind__v" style={{ color: 'var(--ok)' }}>{health.green}</p></div>
          </div>
          <div className="ind">
            <div className="ind__head"><p className="ind__abbr">AMBER</p><p className="ind__full">Watch</p></div>
            <div className="ind__value"><p className="ind__v" style={{ color: 'var(--warn)' }}>{health.amber}</p></div>
          </div>
          <div className="ind">
            <div className="ind__head"><p className="ind__abbr">RED</p><p className="ind__full">Off target</p></div>
            <div className="ind__value"><p className="ind__v" style={{ color: 'var(--risk)' }}>{health.red}</p></div>
          </div>
        </div>

        <div className="box" style={{ marginTop: 'var(--sp-4)' }}>
          <div className="box__head"><h3 className="box__title">Indicator scorecard</h3></div>
          <div className="box__body" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 'var(--sp-3)' }}>
            {kpis.map((kpi) => (
              <button
                key={kpi.key}
                className="box"
                style={{
                  textAlign: 'left', cursor: 'pointer',
                  borderLeft: `3px solid var(--${kpi.status === 'green' ? 'ok' : kpi.status === 'amber' ? 'warn' : 'risk'})`,
                }}
                onClick={() => setSelectedKpi(kpi.key === selectedKpi ? null : kpi.key)}
              >
                <p className="ind__abbr">{kpi.label}</p>
                <p className="ind__v">{formatValue(kpi)}</p>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-45)' }}>Target {kpi.target}{kpi.unit === 'pct' ? '%' : ` ${kpi.unit}`}</p>
              </button>
            ))}
          </div>
        </div>

        {detail && (
          <div className="box" style={{ marginTop: 'var(--sp-4)' }}>
            <div className="box__head"><h3 className="box__title">{detail.label} — trend</h3></div>
            <div className="box__body">
              <table className="tbl">
                <thead><tr><th>Week</th><th>Value</th></tr></thead>
                <tbody>
                  {detail.series.map((p) => (
                    <tr key={p.period_start}><td>{p.period_start}</td><td>{p.value}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 'var(--sp-4)', marginTop: 'var(--sp-4)' }}>
          <div className="box">
            <div className="box__head"><h3 className="box__title">Signals</h3></div>
            <div className="box__body">
              {signals.length === 0 && <p style={{ color: 'var(--ink-45)' }}>No anomalies against the 8-week baseline.</p>}
              {signals.map((s) => (
                <div key={s.kpi_key} className="notice notice--warn" style={{ marginBottom: 'var(--sp-2)' }}>
                  <p className="notice__body">{s.message}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="box">
            <div className="box__head"><h3 className="box__title">Event correlation</h3></div>
            <div className="box__body">
              <p style={{ fontSize: 'var(--text-sm)' }}>
                {correlation.raw_count} raw tickets in the last 24h correlated into {correlation.correlated_count} situations.
              </p>
            </div>
          </div>
        </div>

        <div className="box" style={{ marginTop: 'var(--sp-4)' }}>
          <div className="box__head"><h3 className="box__title">Benchmarks</h3></div>
          <div className="box__body">
            <p className="cap" style={{ marginBottom: 'var(--sp-2)' }}>Peer band is notional reference data for federal IT service desks of comparable size.</p>
            <table className="tbl">
              <thead><tr><th>KPI</th><th>Peer low</th><th>Peer high</th></tr></thead>
              <tbody>
                {benchmarks.map((b) => (
                  <tr key={b.kpi_key}><td>Peer band — {b.label}</td><td>{b.peer_low}</td><td>{b.peer_high}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
